import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { msalInstance } from 'app/config';
import { ApiStatusCodes } from 'common/constants/apiStatusCodes';

import { APIBasePaginatedResponse, APIBaseResponse, APIError } from './apiTypes';

const PARAMETERS_IGNORED = ['tenantId', 'signal', 'rowsCount', 'retries', 'maxNumberOfEntitiesPerRequest'];

export const handleListFetch = async <T>(promise: Promise<APIBaseResponse<Array<T>> | APIBasePaginatedResponse<Array<T>>> ): Promise<T[]> => {
    try {
        return (await promise).data;
    }
    catch (err){
        if (err instanceof APIError && err.message.includes('404')) {
            return [];
        }
        throw err;
    }
};

export const extractApiErrorMessage = (error: unknown): string => {
    const errorInstance = error as Error;
    const { message } = errorInstance;

    return message ?? '';
};

export const extractStatusCode = (error: string): number => {
    let statusCodeString = error.replace('Internal API error: ', '');
    if (statusCodeString && statusCodeString.length > 2) {
        statusCodeString = statusCodeString.substring(0,3);
        return Number(statusCodeString);
    }
    return 0;
};

export const getJWT = (request: SilentRequest) => {
    if (request.account) {
        return msalInstance.acquireTokenSilent(request)
            .catch((error: Error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return msalInstance.acquireTokenRedirect(request);
                }
                throw error;
            });
    }
    else {
        return msalInstance.loginRedirect(request);
    }
};

export const is404Error = (error: string): boolean => {
    const errorCode = extractStatusCode(error);
    return errorCode === ApiStatusCodes.NOT_FOUND;
};

export const isSomethingWentWrongError = (error: string): boolean => {
    const errorCode = extractStatusCode(error);
    return Boolean(errorCode && errorCode !== ApiStatusCodes.OK);
};

export const buildQueryParams = <TParams extends Record<string, unknown>>(params: TParams, multiQueryParams:{
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getValue?: (value : any) => string | number }[] = []
) => {
    const hasValue = (value: unknown) => value || value === 0 || value === false;

    const singleQueryParams = Object.getOwnPropertyNames(params)
        .filter(key => hasValue(params[key as keyof typeof params]) && PARAMETERS_IGNORED.every(item => item !== key) && multiQueryParams?.every(item => item.key !== key))
        .map(key => {
            const param = params[key as keyof typeof params] as string | number | boolean;
            return getQuertStringForKeyAndValue(key, param);
        }).join('&');

    const multiQueryParamsQueryString = multiQueryParams?.map(param => {
        const multiQueryParamsValue = params[param.key as keyof typeof params] as [];
        return multiQueryParamsValue?.map(value => getQuertStringForKeyAndValue(param.key, getValue(value, param.getValue))).join('&');
    }).filter(str => str).join('&');

    return multiQueryParamsQueryString ? `${singleQueryParams}&${multiQueryParamsQueryString}` : singleQueryParams;
};

const getQuertStringForKeyAndValue = (key:string, value:string | number | boolean) => `${key}=${encodeURIComponent(value)}`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getValue = (value: string | number, paramGetValue?: (value: any) => string | number) => paramGetValue ? paramGetValue(value) : value;
